import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Search, Download, Filter } from "lucide-react";
import api, { errMsg } from "@/lib/api";

const CH_STYLE = { Atiti: "bg-[#EEF2FF] text-[#4F46E5]", "Booking.com": "bg-sky-50 text-sky-600", Airbnb: "bg-violet-50 text-violet-600", Direct: "bg-emerald-50 text-emerald-600" };
const ST_STYLE = {
  upcoming: "bg-amber-50 text-amber-600", ongoing: "bg-emerald-50 text-emerald-600",
  checked_in: "bg-emerald-100 text-emerald-700", checked_out: "bg-slate-100 text-slate-500",
  completed: "bg-slate-100 text-slate-500", cancelled: "bg-red-50 text-red-500", no_show: "bg-orange-50 text-orange-500",
};
const fmt = (n) => "₹" + Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 });

export default function CRSReservations({ setRowClick }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [channel, setChannel] = useState("");
  const [pid, setPid] = useState("");

  const load = useCallback(() => {
    setData(null);
    api.get("/pms/reservations/crs", { params: { q, status, channel, pid } })
      .then(({ data: d }) => setData(d))
      .catch((e) => toast.error(errMsg(e)));
  }, [q, status, channel, pid]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    if (!data) return;
    const head = ["Property", "Guest", "Res No", "Channel", "Check-in", "Check-out", "Nights", "Total", "Paid", "ADR", "Status"];
    const lines = [head.join(",")];
    data.reservations.forEach((r) => lines.push([
      `"${r.property_name}"`, `"${r.guest}"`, r.id.slice(0, 8), r.channel, r.checkin, r.checkout,
      r.nights, r.amount, r.paid, r.adr, r.status].join(",")));
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `atiti-crs-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
    toast.success(`Exported ${data.reservations.length} reservations`);
  };

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-slate-300" size={30} /></div>;

  return (
    <div className="reveal space-y-4">
      <div className="bg-white rounded-2xl border border-slate-200 px-5 py-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-[240px] bg-slate-50 rounded-full px-4 py-2 border border-slate-100">
          <Search size={14} className="text-slate-400" />
          <input data-testid="crs-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search guest, property, unit, channel…" className="w-full bg-transparent text-sm focus:outline-none" />
        </div>
        <select data-testid="crs-property" value={pid} onChange={(e) => setPid(e.target.value)} className="text-xs border border-slate-200 rounded-full px-3 py-2 bg-white focus:outline-none">
          <option value="">All properties ({data.properties.length})</option>
          {data.properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select data-testid="crs-channel" value={channel} onChange={(e) => setChannel(e.target.value)} className="text-xs border border-slate-200 rounded-full px-3 py-2 bg-white focus:outline-none">
          <option value="">All channels</option>
          {data.channels.map((c) => <option key={c}>{c}</option>)}
        </select>
        <select data-testid="crs-status" value={status} onChange={(e) => setStatus(e.target.value)} className="text-xs border border-slate-200 rounded-full px-3 py-2 bg-white focus:outline-none">
          <option value="">All statuses</option>
          {["upcoming", "checked_in", "checked_out", "ongoing", "completed", "cancelled", "no_show"].map((s) => <option key={s}>{s}</option>)}
        </select>
        <button data-testid="crs-export" onClick={exportCsv} className="text-xs px-4 py-2 border border-slate-200 rounded-full hover:border-slate-400 flex items-center gap-1.5"><Download size={12} /> Export</button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 bg-slate-50/60">
          <span className="font-display font-bold text-slate-900 text-sm">CRS Reservations</span>
          <span className="text-[11px] text-slate-500 font-semibold">{data.reservations.length} of {data.count}</span>
        </div>
        <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white z-10">
              <tr className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                <th className="text-left px-5 py-3 font-semibold">Property</th>
                <th className="text-left px-3 py-3 font-semibold">Guest</th>
                <th className="text-left px-3 py-3 font-semibold">Res No</th>
                <th className="text-left px-3 py-3 font-semibold">Channel</th>
                <th className="text-left px-3 py-3 font-semibold">Arrival / Departure</th>
                <th className="text-right px-3 py-3 font-semibold">Total</th>
                <th className="text-right px-3 py-3 font-semibold">Paid</th>
                <th className="text-right px-3 py-3 font-semibold">ADR</th>
                <th className="text-right px-5 py-3 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.reservations.map((r) => (
                <tr key={r.id} data-testid={`crs-row-${r.id}`} onClick={() => setRowClick && setRowClick({ ...r, pid: r.property_id })}
                  className="border-b border-slate-50 hover:bg-slate-50/70 cursor-pointer">
                  <td className="px-5 py-3">
                    <p className="font-semibold text-slate-800 text-[13px] truncate max-w-[240px]" title={r.property_name}>{r.property_name}</p>
                    <p className="text-[11px] text-slate-400">{r.property_city}</p>
                  </td>
                  <td className="px-3 py-3">
                    <p className="font-semibold text-slate-800">{r.guest}</p>
                    <p className="text-[10px] text-slate-400">Unit {r.unit_name}</p>
                  </td>
                  <td className="px-3 py-3 font-mono text-xs text-slate-500">{r.id.slice(0, 8).toUpperCase()}</td>
                  <td className="px-3 py-3"><span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${CH_STYLE[r.channel] || "bg-slate-100 text-slate-500"}`}>{r.channel}</span></td>
                  <td className="px-3 py-3 font-mono text-xs text-slate-600">
                    <div>{r.checkin}</div>
                    <div className="text-slate-400">{r.checkout} <span className="text-slate-300">({r.nights}n)</span></div>
                  </td>
                  <td className="px-3 py-3 text-right font-mono text-slate-700">{fmt(r.amount)}</td>
                  <td className="px-3 py-3 text-right font-mono text-slate-500">{fmt(r.paid)}</td>
                  <td className="px-3 py-3 text-right font-mono text-slate-500">{fmt(r.adr)}</td>
                  <td className="px-5 py-3 text-right"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${ST_STYLE[r.status] || "bg-slate-100 text-slate-500"}`}>{r.status}</span></td>
                </tr>
              ))}
              {data.reservations.length === 0 && (
                <tr><td colSpan="9" className="px-6 py-12 text-center text-sm text-slate-400">No reservations match your filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
